import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import ErrorBoundary, { CheckoutCrashed } from './components/common/ErrorBoundary';
import { useEffect, useState } from 'react';
import { captureUrlParams } from './utils/urlParams';
import { rememberCouponFromSearch } from './utils/pendingCoupon';
import { initGoogleAds } from './utils/googleAds';
import { initMetaAds } from './utils/metaAds';

import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { WishlistProvider } from './context/WishlistContext';
import { MarketProvider } from './context/MarketContext';
import { ReviewsProvider } from './context/ReviewsContext';
import { OffersProvider } from './context/OffersContext';
import MarketBanner from './components/market/MarketBanner';
import ProtectedRoute from './routes/ProtectedRoute';
import Layout from './components/layout/Layout';
import CheckoutModal from './components/checkout/CheckoutModal';

// Pages
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import BrandsPage from './pages/BrandsPage';
import SearchPage from './pages/SearchPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import CollectionPage from './pages/CollectionPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import TrackOrderPage from './pages/TrackOrderPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AccountPage from './pages/AccountPage';
import WishlistPage from './pages/WishlistPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import OfferPage from './pages/OfferPage';
import ReviewLinkPage from './pages/ReviewLinkPage';
import {
  ReturnPolicyPage, PrivacyPolicyPage, TermsPage,
  ShippingPage, CancellationPage, SitemapPage, NotFoundPage
} from './pages/StaticPages';
import { useMarket } from './context/MarketContext';

// ── Market-prefixed storefront (/us, /us/shop, /us/product/:handle, ...) ────
// Matches any /:prefix/* that isn't already handled by a static route above.
// Looks up the prefix in availableMarkets; if found sets the market context
// and renders the full storefront; if not found renders 404.
function MarketStorefront() {
  const { marketPrefix } = useParams();
  const { availableMarkets, marketsLoaded, setCurrentMarket, currentMarket } = useMarket();
  const [resolved, setResolved] = useState(null); // null=pending, false=not found, obj=market

  useEffect(() => {
    if (!marketsLoaded) return;
    const match = availableMarkets.find(m => {
      const prefix = (m.urlPrefix || '').replace(/^\//, '').toLowerCase();
      return prefix !== '' && prefix === marketPrefix.toLowerCase();
    });
    setResolved(match || false);
    if (match && match.code !== currentMarket?.code) {
      setCurrentMarket(match);
    }
  }, [marketsLoaded, availableMarkets, marketPrefix]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!marketsLoaded || resolved === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (resolved === false) {
    return <Layout><NotFoundPage /></Layout>;
  }

  // Render the full storefront under the market prefix.
  // React Router v6 automatically strips the matched prefix so these paths
  // are relative to /:marketPrefix (i.e. "shop" matches "/us/shop").
  return (
    <Routes>
      <Route index element={<Layout><HomePage /></Layout>} />
      <Route path="shop" element={<Layout><ShopPage /></Layout>} />
      <Route path="offers/:code" element={<Layout><OfferPage /></Layout>} />
      <Route path="review" element={<Layout><ReviewLinkPage /></Layout>} />
      <Route path="product/:handle" element={<Layout><ProductDetailPage /></Layout>} />
      <Route path="brands" element={<Layout><BrandsPage /></Layout>} />
      <Route path="search" element={<Layout><SearchPage /></Layout>} />
      <Route path="cart" element={<Layout><CartPage /></Layout>} />
      <Route path="checkout" element={<Layout><CheckoutPage /></Layout>} />
      <Route path="collections/:handle" element={<Layout><CollectionPage /></Layout>} />
      <Route path="collections" element={<Navigate to={`/${marketPrefix}/brands`} replace />} />
      <Route path="order-success/:orderId" element={<Layout><OrderSuccessPage /></Layout>} />
      <Route path="track-order" element={<Layout><TrackOrderPage /></Layout>} />
      <Route path="wishlist" element={<Layout><WishlistPage /></Layout>} />
      <Route path="contact" element={<Layout><ContactPage /></Layout>} />
      <Route path="about" element={<Layout><AboutPage /></Layout>} />
      <Route path="return-policy" element={<Layout><ReturnPolicyPage /></Layout>} />
      <Route path="privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
      <Route path="terms" element={<Layout><TermsPage /></Layout>} />
      <Route path="shipping" element={<Layout><ShippingPage /></Layout>} />
      <Route path="cancellation" element={<Layout><CancellationPage /></Layout>} />
      <Route path="login" element={<LoginPage />} />
      <Route path="register" element={<RegisterPage />} />
      <Route path="forgot-password" element={<ForgotPasswordPage />} />
      <Route path="account/*" element={
        <ProtectedRoute><Layout><AccountPage /></Layout></ProtectedRoute>
      } />
      <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
    </Routes>
  );
}

/** A crash in one page shows a message there; going to another page clears it. */
function PageBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AttributionCapture() {
  const location = useLocation();
  useEffect(() => { captureUrlParams(); }, [location.search]);
  return null;
}

// "?coupon=CODE" on any page (the reorder e-mail's "Buy again"): remembered, then applied once the bag has items.
function CouponLinkCapture() {
  const location = useLocation();
  useEffect(() => { rememberCouponFromSearch(location.search); }, [location.search]);
  return null;
}

function GoogleAdsInit() {
  useEffect(() => {
    initGoogleAds();
    initMetaAds();
  }, []);
  return null;
}

// ── Market prefix guard ───────────────────────────────────────────────────────
// When the active market has a URL prefix (e.g. "/us"), every navigation to an
// un-prefixed storefront path is silently redirected to the prefixed version.
// This means no <Link> component anywhere needs to know about the market prefix.
const SKIP_PREFIX = ['/admin', '/login', '/register', '/forgot-password'];

function MarketPrefixGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentMarket, marketsLoaded, availableMarkets } = useMarket();

  useEffect(() => {
    const path = location.pathname;

    // Root path "/" handling:
    // - If the active market has a URL prefix, redirect to the market home (e.g. /us).
    // - If the active market has no URL prefix (content-only market), leave URL as-is.
    // - If somehow an invalid non-default market is stored, reset to default.
    if (path === '/') {
      if (currentMarket && !currentMarket.isDefault) {
        const raw = (currentMarket.urlPrefix || '').replace(/^\//, '').toLowerCase();
        if (raw) {
          const isValidMarket = availableMarkets.some(m => {
            const p = (m.urlPrefix || '').replace(/^\//, '').toLowerCase();
            return p === raw && m.isEnabled;
          });
          if (isValidMarket && marketsLoaded) {
            navigate(`/${raw}`, { replace: true });
          }
        }
        // If no urlPrefix, the content changes via marketCode query param — URL stays at /
      }
      return;
    }

    // Don't touch admin / auth routes
    if (SKIP_PREFIX.some(skip => path.startsWith(skip))) return;

    // Wait until the market list is confirmed from the API before redirecting.
    // Prevents acting on a stale localStorage value from a previous session.
    if (!marketsLoaded) return;

    const raw = (currentMarket?.urlPrefix || '').replace(/^\//, '').toLowerCase();
    if (!raw) return; // default market — nothing to do

    // Verify the stored market is still a valid, enabled market.
    const isValidMarket = availableMarkets.some(m => {
      const p = (m.urlPrefix || '').replace(/^\//, '').toLowerCase();
      return p === raw && m.isEnabled;
    });
    if (!isValidMarket) return;

    const alreadyPrefixed = path === `/${raw}` || path.startsWith(`/${raw}/`);
    if (alreadyPrefixed) return;

    // Redirect to prefixed version, preserving query string, hash AND router state
    navigate(`/${raw}${path}${location.search}${location.hash}`, { replace: true, state: location.state });
  }, [location.pathname, currentMarket?.urlPrefix, marketsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

const STORE_ADMIN_URL = 'https://store-admin-frontend.netlify.app/store/skinnthrive/login';

function LegacyAdminRedirect() {
  useEffect(() => { window.location.replace(STORE_ADMIN_URL); }, []);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AttributionCapture />
        <CouponLinkCapture />
        <GoogleAdsInit />
        <ScrollToTop />
        <MarketProvider>
        <MarketPrefixGuard />
        <ReviewsProvider>
        <OffersProvider>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <MarketBanner />
              <ErrorBoundary fallback={<CheckoutCrashed />}><CheckoutModal /></ErrorBoundary>
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 3000,
                  style: { borderRadius: '12px', fontSize: '14px' },
                  success: { iconTheme: { primary: '#6B0F6E', secondary: '#fff' } },
                }}
              />
              <PageBoundary>
              <Routes>
                {/* Routes with layout */}
                <Route element={<Layout><HomePage /></Layout>} path="/" />
                <Route element={<Layout><ShopPage /></Layout>} path="/shop" />
                <Route element={<Layout><ProductDetailPage /></Layout>} path="/product/:handle" />
                <Route element={<Layout><BrandsPage /></Layout>} path="/brands" />
                <Route element={<Layout><SearchPage /></Layout>} path="/search" />
                <Route element={<Layout><CartPage /></Layout>} path="/cart" />
                <Route element={<Layout><CheckoutPage /></Layout>} path="/checkout" />
                <Route element={<Layout><CollectionPage /></Layout>} path="/collections/:handle" />
                <Route path="/collections" element={<Navigate to="/brands" replace />} />
                <Route path="/new-arrivals" element={<Navigate to="/collections/new-arrivals" replace />} />
                <Route element={<Layout><OrderSuccessPage /></Layout>} path="/order-success/:orderId" />
                <Route element={<Layout><TrackOrderPage /></Layout>} path="/track-order" />
                <Route element={<Layout><WishlistPage /></Layout>} path="/wishlist" />
                <Route element={<Layout><ContactPage /></Layout>} path="/contact" />
                <Route element={<Layout><AboutPage /></Layout>} path="/about" />
                <Route element={<Layout><OfferPage /></Layout>} path="/offers/:code" />
                <Route element={<Layout><ReviewLinkPage /></Layout>} path="/review" />
                <Route element={<Layout><ReturnPolicyPage /></Layout>} path="/return-policy" />
                <Route element={<Layout><PrivacyPolicyPage /></Layout>} path="/privacy-policy" />
                <Route element={<Layout><TermsPage /></Layout>} path="/terms" />
                <Route element={<Layout><ShippingPage /></Layout>} path="/shipping" />
                <Route element={<Layout><CancellationPage /></Layout>} path="/cancellation" />
                <Route element={<Layout><SitemapPage /></Layout>} path="/sitemap" />

                {/* Auth pages */}
                <Route element={<LoginPage />} path="/login" />
                <Route element={<RegisterPage />} path="/register" />
                <Route element={<ForgotPasswordPage />} path="/forgot-password" />

                {/* Protected account */}
                <Route path="/account/*" element={
                  <ProtectedRoute>
                    <Layout><AccountPage /></Layout>
                  </ProtectedRoute>
                } />

                {/* Market-prefixed storefront — must come AFTER all static routes */}
                <Route path="/:marketPrefix/*" element={<MarketStorefront />} />

                <Route element={<Layout><NotFoundPage /></Layout>} path="*" />

                {/* The old built-in admin is retired: the store is managed in the separate store admin. */}
                <Route path="/admin/*" element={<LegacyAdminRedirect />} />
              </Routes>
              </PageBoundary>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
        </OffersProvider>
        </ReviewsProvider>
        </MarketProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
